import { useMemo } from 'react'
import { InferGetServerSidePropsType } from 'next'

import { ssrProps } from '@utils/server'
import { VenueEventMeta } from '@modules/event/views/event-meta'
import { CheckoutForm } from '@modules/booking/controllers/checkout-form'
import { serveBookingCheckoutPageProps } from '@modules/booking/data/server'
import { VenuePageProvider } from '@modules/venue/controllers/venue-page-provider'
import { NoIndexHeader } from '@components/no-index-header'
import { SWRConfig } from 'swr'
import { VenuePageNavigation } from '@modules/venue/views/venue-page-navigation'

export default function OrderCheckoutPage({
  venue,
  integrations,
  booking,
  inventory,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const initialValues = useMemo(
    () => ({
      venue,
      booking,
      bookingId: booking.id,
      inventory,
    }),
    [booking, inventory, venue]
  )

  const breadcrumbs = useMemo(() => {
    const crumbs = [
      {
        label: 'Venue',
        href: `/v/${venue.slug}`,
      },
      {
        label: 'All Events',
        href: `/v/${venue.slug}/events`,
      },
    ]
    if (booking.event)
      crumbs.push({
        label: 'Event',
        href: `/e/${booking.event}`,
      })
    crumbs.push({
      label: 'Order',
      href: `/o/${booking.id}`,
    })
    return crumbs
  }, [booking.event, booking.id, venue.slug])

  return (
    <SWRConfig value={{ revalidateOnMount: true }}>
      <VenueEventMeta venue={venue} titlePrefix='Booking | ' />
      <NoIndexHeader />
      <VenuePageProvider
        venue={venue}
        checkoutValues={initialValues}
        eventPageBanner
        integrations={integrations}
      >
        <div className='mx-auto lg:flex'>
          <div className='lg:w-1/2 sm:space-y-6 lg:bg-card lg:dark:bg-inherit'>
            <div className='flex items-center justify-start w-full max-w-2xl mx-auto lg:max-w-3xl lg:ml-auto lg:mr-0 2xl:pl-8'>
              <VenuePageNavigation
                breadcrumbs={breadcrumbs}
                className='lg:bg-card lg:dark:bg-inherit sm:!px-3 lg:!px-8'
              />
            </div>
          </div>
          <div className='hidden lg:block lg:dark:bg-neutral-900 lg:w-1/2' />
        </div>
        <CheckoutForm venue={venue} />
      </VenuePageProvider>
    </SWRConfig>
  )
}

export const getServerSideProps = ssrProps(serveBookingCheckoutPageProps)
